import React from 'react';
import PropTypes from 'prop-types';
import './Icon.css';

/**
 * Icon wrapper component with Cyberpunk 2077 styling and theme support
 * @param {string} name - Icon name
 * @param {number} size - Icon size in pixels (default: 20)
 * @param {string} className - Additional CSS classes
 * @param {string} color - Custom color (overrides theme)
 * @param {boolean} glow - Enable neon glow effect
 * @param {function} onClick - Click handler
 */
const Icon = ({name, size = 20, className = '', color, glow = true, onClick, ...props}) => {
  const IconComponent = iconMap[name];
  
  if (!IconComponent) {
    return null;
  }

  return (
    <span
      {...props}
      className={`cyber-icon ${glow ? 'cyber-icon-glow' : ''} ${className}`}
      onClick={onClick}
      style={{ 
        width: size, 
        height: size,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(color && {'--icon-color': color})
      }}
    >
      <IconComponent size={size} />
    </span>
  );
};

Icon.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  glow: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  size: PropTypes.number,
};

// Icon SVG Components with Cyberpunk 2077 aesthetic
const OrganizationIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 3L12 3L21 3V21H16V15H8V21H3V3Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M7 7H9V9H7V7Z M11 7H13V9H11V7Z M15 7H17V9H15V7Z" fill="currentColor"/>
    <path d="M7 11H9V13H7V11Z M11 11H13V13H11V11Z M15 11H17V13H15V11Z" fill="currentColor"/>
    <line stroke="currentColor" strokeWidth="1.5" x1="12" x2="12" y1="1" y2="3"/>
    <circle className="cyber-pulse" cx="12" cy="1" fill="currentColor" r="1"/>
  </svg>
);

OrganizationIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const FolderIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 5L11 5L13 7H21V19H3V5Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M3 7H21" stroke="currentColor" strokeWidth="1"/>
    <circle cx="6" cy="12" fill="currentColor" opacity="0.6" r="1"/>
    <line opacity="0.4" stroke="currentColor" strokeWidth="1" x1="9" x2="18" y1="12" y2="12"/>
    <line opacity="0.4" stroke="currentColor" strokeWidth="1" x1="9" x2="15" y1="15" y2="15"/>
  </svg>
);

FolderIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const FileIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 2H15L19 6V22H5V2Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M15 2V6H19" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <line opacity="0.6" stroke="currentColor" strokeWidth="1" x1="8" x2="16" y1="10" y2="10"/>
    <line opacity="0.6" stroke="currentColor" strokeWidth="1" x1="8" x2="16" y1="13" y2="13"/>
    <line opacity="0.6" stroke="currentColor" strokeWidth="1" x1="8" x2="13" y1="16" y2="16"/>
  </svg>
);

FileIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const FilePlusIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 2H15L19 6V22H5V2Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M15 2V6H19" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <line stroke="currentColor" strokeWidth="1.5" x1="12" x2="12" y1="11" y2="17"/>
    <line stroke="currentColor" strokeWidth="1.5" x1="9" x2="15" y1="14" y2="14"/>
  </svg>
);

FilePlusIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const FolderPlusIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 5L11 5L13 7H21V19H3V5Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M3 7H21" stroke="currentColor" strokeWidth="1"/>
    <line stroke="currentColor" strokeWidth="1.5" x1="12" x2="12" y1="10" y2="16"/>
    <line stroke="currentColor" strokeWidth="1.5" x1="9" x2="15" y1="13" y2="13"/>
  </svg>
);

FolderPlusIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CodeFileIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M5 2H15L19 6V22H5V2Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M15 2V6H19" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <path d="M8 12L10 14L8 16" stroke="currentColor" strokeLinecap="square" strokeWidth="1.5"/>
    <path d="M16 12L14 14L16 16" stroke="currentColor" strokeLinecap="square" strokeWidth="1.5"/>
    <line stroke="currentColor" strokeWidth="1.5" x1="11" x2="13" y1="17" y2="11"/>
  </svg>
);

CodeFileIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const RefreshIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 12C4 7.58172 7.58172 4 12 4C14.5 4 16.7 5.1 18.2 6.8L20 5V10H15L17 8C15.9 6.7 14.1 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18C14.5 18 16.6 16.6 17.6 14.5"
      stroke="currentColor"
      strokeLinecap="square"
      strokeLinejoin="bevel"
      strokeWidth="1.5"
    />
    <circle className="cyber-pulse" cx="20" cy="5" fill="currentColor" r="1"/>
  </svg>
);

RefreshIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PackageIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M3 7L12 12M12 12L21 7M12 12V22" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1" fill="none"/>
    <circle cx="12" cy="2" r="1" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

PackageIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const EyeIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z"
      stroke="currentColor"
      strokeLinejoin="bevel"
      strokeWidth="1.5"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
    <line x1="5" y1="7" x2="6" y2="8" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
    <line x1="19" y1="7" x2="18" y2="8" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
  </svg>
);

EyeIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const WarningIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L3 20H21L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <circle cx="12" cy="16" r="1" fill="currentColor"/>
    <path d="M12 2L12 0" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="0" r="0.5" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

WarningIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const TrashIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M8 6V4H16V6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M5 6V20C5 21 6 22 7 22H17C18 22 19 21 19 20V6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <line x1="10" y1="10" x2="10" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <line x1="14" y1="10" x2="14" y2="18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
  </svg>
);

TrashIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const DesignIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="1"/>
    <path d="M12 3V12M12 12L19 16M12 12L5 16" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="3" r="1" fill="currentColor"/>
    <circle cx="19" cy="16" r="1" fill="currentColor"/>
    <circle cx="5" cy="16" r="1" fill="currentColor"/>
  </svg>
);

DesignIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const AnalyzeIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M7 17V13" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <path d="M12 17V9" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <path d="M17 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <circle cx="7" cy="13" r="1" fill="currentColor"/>
    <circle cx="12" cy="9" r="1" fill="currentColor"/>
    <circle cx="17" cy="7" r="1" fill="currentColor"/>
    <path d="M7 13L12 9L17 7" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
  </svg>
);

AnalyzeIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PlanIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="3" width="14" height="18" rx="1" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M9 7H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M9 11H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M9 15H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <rect x="8" y="1" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.6"/>
    <rect x="13" y="1" width="3" height="4" rx="0.5" fill="currentColor" opacity="0.6"/>
    <circle cx="7" cy="7" r="0.5" fill="currentColor"/>
    <circle cx="7" cy="11" r="0.5" fill="currentColor"/>
    <circle cx="7" cy="15" r="0.5" fill="currentColor"/>
  </svg>
);

PlanIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const TroubleshootIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.7 6.3L19 2M14.7 6.3C13.2 4.8 10.8 4.8 9.3 6.3C7.8 7.8 7.8 10.2 9.3 11.7L14.7 17.1C16.2 18.6 18.6 18.6 20.1 17.1C21.6 15.6 21.6 13.2 20.1 11.7L14.7 6.3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="bevel"/>
    <path d="M3 21L9 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <circle cx="19" cy="2" r="1" fill="currentColor"/>
    <path d="M6 18L8 16" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
  </svg>
);

TroubleshootIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const LinkIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 6L8 6C5.79086 6 4 7.79086 4 10V10C4 12.2091 5.79086 14 8 14L10 14"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
    <path
      d="M14 18L16 18C18.2091 18 20 16.2091 20 14V14C20 11.7909 18.2091 10 16 10L14 10"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
    <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="8" cy="12" r="1.5" fill="currentColor"/>
    <circle cx="16" cy="12" r="1.5" fill="currentColor"/>
  </svg>
);

LinkIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const SettingsIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M12 2L10 6L6 6L8 10L4 12L8 14L6 18L10 18L12 22L14 18L18 18L16 14L20 12L16 10L18 6L14 6L12 2Z"
      stroke="currentColor"
      strokeLinejoin="bevel"
      strokeWidth="1.5"
    />
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

SettingsIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ToolsIcon = ({size}) => (
  <svg width="100" height="100" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.5 1L21.5 8L19.5 10L18.5 9L14 13.5L10.5 10L15 5.5L14 4.5L16 2.5L14.5 1Z" fill="#dfdfdfF" stroke="#dfdfdf" strokeWidth="0.5"/>
    <path d="M18.5 9L14 13.5L10.5 10L15 5.5L18.5 9Z" fill="#dfdfdfF" stroke="#dfdfdf" strokeWidth="1.3"/>
    
    <rect x="5.1" y="14.6" width="12" height="3.5" rx="1.5" transform="rotate(-45 5.1 14.6)" fill="#dfdfdf"/>
    
    <rect x="3.7" y="16" width="5" height="3.5" rx="1" transform="rotate(-45 3.7 16)" fill="#dfdfdf"/>
  </svg>
);

ToolsIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Plug/connector icon representing an MCP (Model Context Protocol) server
const McpServerIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.1073 2.73542C12.6101 1.37766 14.9305 1.4229 16.3788 2.87116C17.1707 3.6631 17.543 4.71591 17.496 5.753C18.5334 5.70566 19.5867 6.079 20.3788 6.87116L20.5146 7.01472C21.8283 8.46908 21.8284 10.6884 20.5146 12.1426L20.3788 12.2852L13.414 19.2501L14.707 20.543L14.7753 20.6192C15.0956 21.012 15.073 21.591 14.707 21.9571C14.3409 22.3231 13.7618 22.3457 13.3691 22.0255L13.2929 21.9571L11.9999 20.6641C11.2191 19.8831 11.2191 18.617 11.9999 17.836L18.9648 10.8712L19.0898 10.7325C19.6365 10.0622 19.6364 9.0952 19.0898 8.42487L18.9648 8.28523C18.2954 7.61585 17.2356 7.57438 16.5175 8.16023L16.3788 8.28523L10.207 14.4571C9.81642 14.8476 9.18341 14.8476 8.79289 14.4571C8.40237 14.0666 8.40237 13.4336 8.79289 13.043L14.9648 6.87116L15.0898 6.73249C15.6756 6.01432 15.6342 4.95468 14.9648 4.28523C14.2954 3.61585 13.2356 3.57438 12.5175 4.16023L12.3788 4.28523L4.20695 12.4571C3.81643 12.8476 3.18341 12.8476 2.79289 12.4571C2.40237 12.0666 2.40237 11.4336 2.79289 11.043L10.9648 2.87116L11.1073 2.73542Z" fill="currentColor"/>
    <path d="M11.8925 16.764C10.3897 18.1217 8.06924 18.0765 6.62097 16.6282C5.17273 15.1799 5.12756 12.8596 6.48523 11.3567L6.62097 11.2142L12.7928 5.04229C13.1834 4.65177 13.8164 4.65177 14.2069 5.04229C14.5974 5.43282 14.5974 6.06583 14.2069 6.45635L8.03504 12.6282L7.91004 12.7669C7.32427 13.4851 7.36564 14.5447 8.03504 15.2142C8.70442 15.8836 9.76421 15.925 10.4823 15.3392L10.621 15.2142L16.7928 9.04229C17.1834 8.65176 17.8164 8.65177 18.2069 9.04229C18.5973 9.43282 18.5974 10.0659 18.2069 10.4564L12.035 16.6282L11.8925 16.764Z" fill="currentColor"/>
  </svg>
);

McpServerIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CodeIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M17 8L21 12L17 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <line x1="14" y1="4" x2="10" y2="20" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="14" cy="4" r="1" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

CodeIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ImageIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <circle cx="8.5" cy="8.5" r="2" stroke="currentColor" strokeWidth="1"/>
    <path d="M3 15L8 10L13 15L18 10L21 13" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <circle cx="8.5" cy="8.5" r="0.5" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

ImageIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const VideoIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="5" width="14" height="14" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M16 9L22 6V18L16 15V9Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <circle cx="9" cy="12" r="3" stroke="currentColor" strokeWidth="1"/>
    <polygon points="10,12 8,13.5 8,10.5" fill="currentColor"/>
  </svg>
);

VideoIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ReactIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="12" rx="9" ry="3.5" stroke="currentColor" strokeWidth="1.5"/>
    <ellipse cx="12" cy="12" rx="9" ry="3.5" stroke="currentColor" strokeWidth="1.5" transform="rotate(60 12 12)"/>
    <ellipse cx="12" cy="12" rx="9" ry="3.5" stroke="currentColor" strokeWidth="1.5" transform="rotate(120 12 12)"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

ReactIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const TypescriptIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M7 8H17M12 8V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path
      d="M14 13C14 13 15 12 16 12C17 12 18 13 18 14C18 16 14 16 14 16"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
    <circle cx="16" cy="12" r="0.5" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

TypescriptIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const JavascriptIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path
      d="M15 16C15 16 16 17 17.5 17C18.5 17 19 16.5 19 16C19 14.5 15 14.5 15 12.5C15 11 16 10 17.5 10C19 10 20 11 20 11"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
    <path
      d="M10 10V15.5C10 16.5 9.5 17 8.5 17C7.5 17 7 16.5 7 16"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
  </svg>
);

JavascriptIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const JsonIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 4C6 4 5 5 5 7V9C5 10 4 11 3 11M3 11C4 11 5 12 5 13V15C5 17 6 18 8 18M3 11C3 11 3 13 3 13"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
    <path
      d="M16 4C18 4 19 5 19 7V9C19 10 20 11 21 11M21 11C20 11 19 12 19 13V15C19 17 18 18 16 18M21 11C21 11 21 13 21 13"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="1.5"
    />
    <circle cx="10" cy="8" r="1" fill="currentColor"/>
    <circle cx="14" cy="8" r="1" fill="currentColor"/>
    <circle cx="10" cy="16" r="1" fill="currentColor"/>
    <circle cx="14" cy="16" r="1" fill="currentColor"/>
  </svg>
);

JsonIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const HtmlIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 3L6 20L12 22L18 20L19 3H5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M8 8H16L15.5 13L12 14L8.5 13L8.3 11" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M9.5 16H14.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="3" r="0.5" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

HtmlIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CssIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 3L6 20L12 22L18 20L19 3H5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <rect x="8" y="8" width="8" height="8" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <line x1="10" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="1"/>
    <line x1="10" y1="12" x2="14" y2="12" stroke="currentColor" strokeWidth="1"/>
    <line x1="10" y1="14" x2="14" y2="14" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

CssIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PythonIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 3H16C18 3 20 5 20 7V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M16 21H8C6 21 4 19 4 17V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <circle cx="9" cy="7" r="1" fill="currentColor"/>
    <circle cx="15" cy="17" r="1" fill="currentColor"/>
    <path d="M4 12H20" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

PythonIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const DatabaseIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="6" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5"/>
    <path
      d="M4 6V18C4 19.6569 7.58172 21 12 21C16.4183 21 20 19.6569 20 18V6"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <ellipse cx="12" cy="12" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="4" y1="12" x2="4" y2="18" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="20" y1="12" x2="20" y2="18" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

DatabaseIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const DockerIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="8" width="3" height="3" stroke="currentColor" strokeWidth="1"/>
    <rect x="7" y="8" width="3" height="3" stroke="currentColor" strokeWidth="1"/>
    <rect x="11" y="8" width="3" height="3" stroke="currentColor" strokeWidth="1"/>
    <rect x="7" y="4" width="3" height="3" stroke="currentColor" strokeWidth="1"/>
    <rect x="11" y="4" width="3" height="3" stroke="currentColor" strokeWidth="1"/>
    <path
      d="M3 11H21C21 13 20 14 18 15C17 15.5 15 16 12 16C9 16 7 15.5 6 15C4 14 3 13 3 11Z"
      stroke="currentColor"
      strokeLinejoin="bevel"
      strokeWidth="1.5"
    />
    <path d="M18 8C19 7 20 6 21 7C22 8 21 9 20 10" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

DockerIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const GitIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12L12 3L21 12L12 21L3 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <circle cx="7" cy="12" r="2" stroke="currentColor" strokeWidth="1"/>
    <circle cx="17" cy="12" r="2" stroke="currentColor" strokeWidth="1"/>
    <line x1="9" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="3" r="1" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

GitIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// GitHub icon (Octocat-inspired mark)
const GitHubIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.34-3.369-1.34-.454-1.154-1.11-1.461-1.11-1.461-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.579.688.481C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
  </svg>
);

GitHubIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// GitLab official icon — exact path from simpleicons.org
const GitLabIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="m23.6004 9.5927-.0337-.0862L20.3.9814a.851.851 0 0 0-.3362-.405.8748.8748 0 0 0-.9997.0539.8748.8748 0 0 0-.29.4399l-2.2055 6.748H7.5375l-2.2057-6.748a.8573.8573 0 0 0-.29-.4412.8748.8748 0 0 0-.9997-.0537.8585.8585 0 0 0-.3362.4049L.4332 9.5015l-.0325.0862a6.0657 6.0657 0 0 0 2.0119 7.0105l.0113.0087.03.0213 4.976 3.7264 2.462 1.8633 1.4995 1.1321a1.0085 1.0085 0 0 0 1.2197 0l1.4995-1.1321 2.4619-1.8633 5.006-3.7489.0125-.01a6.0682 6.0682 0 0 0 2.0094-7.003z"/>
  </svg>
);

GitLabIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Azure DevOps official icon — from Microsoft brand guidelines
const AzureDevOpsIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 6.01v11.98L17.5 22l-7.37-2.61v2.6L6.27 17.9l11.16.87V6.58L22 6.01zM17.06 2l-8.6 3.14L2 9.69V14l2.27 1.97V9.96L17.06 5.5V2z"/>
  </svg>
);

AzureDevOpsIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Bitbucket official icon — from simpleicons.org
const BitbucketIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M.778 1.213a.768.768 0 0 0-.768.892l3.263 19.81c.084.5.515.868 1.022.873H19.95a.772.772 0 0 0 .77-.646l3.27-20.03a.768.768 0 0 0-.768-.891L.778 1.213zM14.52 15.53H9.522L8.17 8.466h7.561l-1.211 7.064z"/>
  </svg>
);

BitbucketIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Google icon — monochrome "G" mark matching the style of other provider icons
const GoogleIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
  </svg>
);

GoogleIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const TerminalIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M6 9L9 12L6 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <line x1="11" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <circle cx="6" cy="6" r="0.5" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

TerminalIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CloseIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <circle cx="6" cy="6" r="1" fill="currentColor"/>
    <circle cx="18" cy="6" r="1" fill="currentColor"/>
    <circle cx="6" cy="18" r="1" fill="currentColor"/>
    <circle cx="18" cy="18" r="1" fill="currentColor"/>
  </svg>
);

CloseIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const FullscreenIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3L9 3M3 3L3 9M3 3L8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M21 3L15 3M21 3L21 9M21 3L16 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M3 21L9 21M3 21L3 15M3 21L8 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M21 21L15 21M21 21L21 15M21 21L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
  </svg>
);

FullscreenIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ExitFullscreenIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3L3 3M9 3L9 9M9 3L4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M15 3L21 3M15 3L15 9M15 3L20 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M9 21L3 21M9 21L9 15M9 21L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M15 21L21 21M15 21L15 15M15 21L20 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
  </svg>
);

ExitFullscreenIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ComputerIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="20" height="14" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/>
    <path d="M12 17L12 21" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="5" y="6" width="14" height="8" stroke="currentColor" strokeWidth="1" opacity="0.5"/>
    <circle cx="12" cy="10" r="2" stroke="currentColor" strokeWidth="1"/>
  </svg>
);

ComputerIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const DocumentIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 2H14L18 6V22H6V2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <path d="M14 2V6H18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    <line x1="9" y1="10" x2="15" y2="10" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="9" y1="13" x2="15" y2="13" stroke="currentColor" strokeWidth="1.5"/>
    <line x1="9" y1="16" x2="12" y2="16" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

DocumentIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const SpinnerIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="cyber-spinner">
    <path d="M12 2L12 6M12 18L12 22M6 12L2 12M22 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/>
    <path
      d="M17.5 6.5L14.5 9.5M9.5 14.5L6.5 17.5M17.5 17.5L14.5 14.5M9.5 9.5L6.5 6.5"
      opacity="0.4"
      stroke="currentColor"
      strokeLinecap="square"
      strokeWidth="2"
    />
    <circle cx="12" cy="2" r="1" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

SpinnerIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const EditIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

EditIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const SparklesIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L13 10L12 18L11 10L12 2Z" fill="currentColor" opacity="0.6"/>
    <path d="M2 12L10 13L18 12L10 11L2 12Z" fill="currentColor" opacity="0.6"/>
    <path d="M6 6L9 9L6 12L3 9L6 6Z" fill="currentColor" opacity="0.3"/>
    <path d="M18 6L21 9L18 12L15 9L18 6Z" fill="currentColor" opacity="0.3"/>
    <path d="M6 18L9 21L6 24L3 21L6 18Z" fill="currentColor" opacity="0.3"/>
    <path d="M18 18L21 21L18 24L15 21L18 18Z" fill="currentColor" opacity="0.3"/>
  </svg>
);

SparklesIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PaperclipIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.5 12.5L12 22C9.5 24.5 5.5 24.5 3 22C0.5 19.5 0.5 15.5 3 13L12.5 3.5C14.5 1.5 17.5 1.5 19.5 3.5C21.5 5.5 21.5 8.5 19.5 10.5L10 20C9 21 7 21 6 20C5 19 5 17 6 16L14.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

PaperclipIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const MicrophoneIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M5 12C5 15.866 8.134 19 12 19C15.866 19 19 15.866 19 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 19V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 22H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

MicrophoneIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ExternalLinkIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ExternalLinkIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ArrowUpIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 19V5M12 5L6 11M12 5L18 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ArrowUpIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const TemplateIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M3 8H21" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 8V21" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="10" y="10" width="3" height="3" fill="currentColor" opacity="0.3"/>
    <rect x="15" y="10" width="4" height="3" fill="currentColor" opacity="0.3"/>
    <rect x="10" y="15" width="9" height="4" fill="currentColor" opacity="0.3"/>
  </svg>
);

TemplateIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const GitBranchIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="6" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="18" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6 9V15" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M6 9C6 9 6 12 9 12H15" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

GitBranchIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const WandIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 4L20 9L9 20L4 15L15 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M10 14L15 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="3" cy="3" r="1" fill="currentColor" className="cyber-pulse"/>
    <circle cx="21" cy="3" r="1" fill="currentColor" className="cyber-pulse"/>
    <circle cx="3" cy="21" r="1" fill="currentColor" className="cyber-pulse"/>
  </svg>
);

WandIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ArrowRightIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ArrowRightIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ArrowLeftIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ArrowLeftIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const SearchIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M16 16L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

SearchIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const StarIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15 9L22 10L17 15L18 22L12 18L6 22L7 15L2 10L9 9L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" opacity="0.2"/>
  </svg>
);

StarIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CircleIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="6" fill="currentColor"/>
  </svg>
);

CircleIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CheckCircleIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 12L10 15L17 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

CheckCircleIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const DownloadIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3V15M12 15L7 10M12 15L17 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 17V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

DownloadIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const UploadIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21V9M12 9L7 14M12 9L17 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 7V5C3 3.9 3.9 3 5 3H19C20.1 3 21 3.9 21 5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

UploadIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const RocketIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Rocket body */}
    <path d="M12 2L15 8L15 16L12 18L9 16L9 8L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel" fill="none"/>
    {/* Nose cone */}
    <path d="M12 2L15 8L12 6L9 8L12 2Z" fill="currentColor" opacity="0.3"/>
    {/* Window */}
    <circle cx="12" cy="10" r="1.5" fill="currentColor"/>
    {/* Left fin */}
    <path d="M9 12L6 15L9 16" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    {/* Right fin */}
    <path d="M15 12L18 15L15 16" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="bevel"/>
    {/* Flames */}
    <path d="M10 18L10 20L9 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 18L12 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M14 18L14 20L15 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    {/* Flame glow */}
    <circle className="cyber-pulse" cx="12" cy="20" r="1" fill="currentColor" opacity="0.6"/>
  </svg>
);

RocketIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const GridIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

GridIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const UsersIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M2 21C2 17.134 5.134 14 9 14C12.866 14 16 17.134 16 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="17" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M22 21C22 18.239 19.761 16 17 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

UsersIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CompassIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M16 8L14 14L8 16L10 10L16 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <circle cx="12" cy="12" r="2" fill="currentColor"/>
  </svg>
);

CompassIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const BookIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 19V6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V19" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M4 19C4 17.8954 4.89543 17 6 17H18C19.1046 17 20 17.8954 20 19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9 8H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M9 12H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

BookIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const GiftIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="8" width="18" height="4" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="5" y="12" width="14" height="9" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 8V21" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 8C12 6 10 4 8 4C6 4 5 5 5 6C5 7 6 8 8 8H12Z" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 8C12 6 14 4 16 4C18 4 19 5 19 6C19 7 18 8 16 8H12Z" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

GiftIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ZapIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" opacity="0.2"/>
  </svg>
);

ZapIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const BellIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

BellIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PlusIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

PlusIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const MessageIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

MessageIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ChevronDownIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ChevronDownIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ChevronUpIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ChevronUpIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ChevronRightIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ChevronRightIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const RobotIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="8" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <circle cx="9" cy="13" r="1.5" fill="currentColor"/>
    <circle cx="15" cy="13" r="1.5" fill="currentColor"/>
    <path d="M9 16H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 8V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="4" r="1" fill="currentColor"/>
    <path d="M5 11H3M21 11H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7 20V22M17 20V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

RobotIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const LoaderIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="60" strokeDashoffset="15" opacity="0.3"/>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="15" className="cyber-spin"/>
  </svg>
);

LoaderIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ClockIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 7V12L15 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ClockIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CalendarIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M3 10H21" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 2V6M16 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

CalendarIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PlayIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5V19L19 12L8 5Z" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

PlayIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PauseIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="4" width="4" height="16" fill="currentColor" rx="1"/>
    <rect x="14" y="4" width="4" height="16" fill="currentColor" rx="1"/>
  </svg>
);

PauseIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ListIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

ListIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const XIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

XIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CheckIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

CheckIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const InfoIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="12" cy="8" r="1" fill="currentColor"/>
  </svg>
);

InfoIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const HomeIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>
);

HomeIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const MenuIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

MenuIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PanelLeftIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9 3V21" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M14 10L12 12L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

PanelLeftIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const PanelRightIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9 3V21" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 10L14 12L12 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

PanelRightIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const SplitIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M12 3V21" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7.5 8.5L7.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M7.5 13.5L7.5 15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16.5 8.5L16.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16.5 13.5L16.5 15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

SplitIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const CameraIcon = ({size}) => (
  <svg
    fill="none"
    height={size}
    viewBox="0 0 24 24"
    width={size}
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect height="14" rx="2" stroke="currentColor" strokeWidth="1.5" width="18" x="3" y="6"/>
    <path d="M9 3H15L17 6H7L9 3Z" stroke="currentColor" strokeLinejoin="bevel" strokeWidth="1.5"/>
    <circle cx="12" cy="13" r="3" stroke="currentColor" strokeWidth="1.5"/>
    <circle className="cyber-pulse" cx="12" cy="13" fill="currentColor" opacity="0.3" r="2"/>
    <circle cx="17.5" cy="9.5" fill="currentColor" r="0.5"/>
  </svg>
);

CameraIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const DollarIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle className="cyber-pulse" cx="12" cy="12" fill="currentColor" opacity="0.2" r="3"/>
  </svg>
);

DollarIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const BarChartIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="12" width="4" height="9" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.2"/>
    <rect x="10" y="8" width="4" height="13" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.2"/>
    <rect x="17" y="3" width="4" height="18" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.2"/>
    <line x1="2" y1="21" x2="22" y2="21" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

BarChartIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ActivityIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

ActivityIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const SequenceIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="7" width="5" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="9.5" y="7" width="5" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <rect x="17" y="7" width="5" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M7 12H9.5" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M14.5 12H17" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="12" cy="12" r="1" fill="currentColor"/>
  </svg>
);

SequenceIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// New Home Omnibar Mode Icons
const MyTasksIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M8 7H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 12H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 17H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="5.5" cy="7" r="0.5" fill="currentColor"/>
    <circle cx="5.5" cy="12" r="0.5" fill="currentColor"/>
    <circle cx="5.5" cy="17" r="0.5" fill="currentColor"/>
    <circle className="cyber-pulse" cx="19" cy="5" r="2" fill="currentColor" opacity="0.6"/>
  </svg>
);

MyTasksIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const BrainstormingIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="10" r="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M10 14H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 17H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M11 20H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 6V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M8 8L7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M16 8L17 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle className="cyber-pulse" cx="12" cy="10" r="2" fill="currentColor" opacity="0.3"/>
  </svg>
);

BrainstormingIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const FullStackIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="3" width="20" height="7" rx="1" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.1"/>
    <rect x="2" y="14" width="20" height="7" rx="1" stroke="currentColor" strokeWidth="1.5" fill="currentColor" opacity="0.1"/>
    <path d="M12 10V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M10 12H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="6" cy="6.5" r="1" fill="currentColor"/>
    <circle cx="6" cy="17.5" r="1" fill="currentColor"/>
    <rect x="9" y="5.5" width="4" height="2" rx="0.5" fill="currentColor" opacity="0.4"/>
    <rect x="9" y="16.5" width="4" height="2" rx="0.5" fill="currentColor" opacity="0.4"/>
  </svg>
);

FullStackIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ImportExistingIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 15V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle className="cyber-pulse" cx="12" cy="3" r="1.5" fill="currentColor"/>
  </svg>
);

ImportExistingIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

const ModernizeIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="currentColor" opacity="0.2"/>
    <circle className="cyber-pulse" cx="13" cy="2" r="1" fill="currentColor"/>
  </svg>
);

ModernizeIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Data Catalog icon — document list with metadata
const DataCatalogIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9H9V9h10v2zm-4 4H9v-2h6v2zm4-8H9V5h10v2z"/>
  </svg>
);

DataCatalogIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// P4SAMD icon — structured document / compliance grid
const P4SamdIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
  </svg>
);

P4SamdIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Atlassian official icon — exact path from simpleicons.org
const AtlassianIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.12 11.084a.683.683 0 00-1.16.126L.075 22.974a.703.703 0 00.63 1.018h8.19a.678.678 0 00.63-.39c1.767-3.65.696-9.203-2.406-12.52zM11.434.386a15.515 15.515 0 00-.906 15.317l3.95 7.9a.703.703 0 00.628.388h8.19a.703.703 0 00.63-1.017L12.63.38a.664.664 0 00-1.196.006z"/>
  </svg>
);

AtlassianIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Grafana official icon — exact path from simpleicons.org
const GrafanaIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.02 10.59a8.578 8.578 0 0 0-.862-3.034 8.911 8.911 0 0 0-1.789-2.445c.337-1.342-.413-2.505-.413-2.505-1.292-.08-2.113.4-2.416.62-.052-.02-.102-.044-.154-.064-.22-.089-.446-.172-.677-.247-.231-.073-.47-.14-.711-.197a9.867 9.867 0 0 0-.875-.161C14.557.753 12.94 0 12.94 0c-1.804 1.145-2.147 2.744-2.147 2.744l-.018.093c-.098.029-.2.057-.298.088-.138.042-.275.094-.413.143-.138.055-.275.107-.41.166a8.869 8.869 0 0 0-1.557.87l-.063-.029c-2.497-.955-4.716.195-4.716.195-.203 2.658.996 4.33 1.235 4.636a11.608 11.608 0 0 0-.607 2.635C1.636 12.677.953 15.014.953 15.014c1.926 2.214 4.171 2.351 4.171 2.351.003-.002.006-.002.006-.005.285.509.615.994.986 1.446.156.19.32.371.488.548-.704 2.009.099 3.68.099 3.68 2.144.08 3.553-.937 3.849-1.173a9.784 9.784 0 0 0 3.164.501h.08l.055-.003.107-.002.103-.005.003.002c1.01 1.44 2.788 1.646 2.788 1.646 1.264-1.332 1.337-2.653 1.337-2.94v-.058c0-.02-.003-.039-.003-.06.265-.187.52-.387.758-.6a7.875 7.875 0 0 0 1.415-1.7c1.43.083 2.437-.885 2.437-.885-.236-1.49-1.085-2.216-1.264-2.354l-.018-.013-.016-.013a.217.217 0 0 1-.031-.02c.008-.092.016-.18.02-.27.011-.162.016-.323.016-.48v-.253l-.005-.098-.008-.135a1.891 1.891 0 0 0-.01-.13c-.003-.042-.008-.083-.013-.125l-.016-.124-.018-.122a6.215 6.215 0 0 0-2.032-3.73 6.015 6.015 0 0 0-3.222-1.46 6.292 6.292 0 0 0-.85-.048l-.107.002h-.063l-.044.003-.104.008a4.777 4.777 0 0 0-3.335 1.695c-.332.4-.592.84-.768 1.297a4.594 4.594 0 0 0-.312 1.817l.003.091c.005.055.007.11.013.164a3.615 3.615 0 0 0 .698 1.82 3.53 3.53 0 0 0 1.827 1.282c.33.098.66.14.971.137.039 0 .078 0 .114-.002l.063-.003c.02 0 .041-.003.062-.003.034-.002.065-.007.099-.01.007 0 .018-.003.028-.003l.031-.005.06-.008a1.18 1.18 0 0 0 .112-.02c.036-.008.072-.013.109-.024a2.634 2.634 0 0 0 .914-.415c.028-.02.056-.041.085-.065a.248.248 0 0 0 .039-.35.244.244 0 0 0-.309-.06l-.078.042c-.09.044-.184.083-.283.116a2.476 2.476 0 0 1-.475.096c-.028.003-.054.006-.083.006l-.083.002c-.026 0-.054 0-.08-.002l-.102-.006h-.012l-.024.006c-.016-.003-.031-.003-.044-.006-.031-.002-.06-.007-.091-.01a2.59 2.59 0 0 1-.724-.213 2.557 2.557 0 0 1-.667-.438 2.52 2.52 0 0 1-.805-1.475 2.306 2.306 0 0 1-.029-.444l.006-.122v-.023l.002-.031c.003-.021.003-.04.005-.06a3.163 3.163 0 0 1 1.352-2.29 3.12 3.12 0 0 1 .937-.43 2.946 2.946 0 0 1 .776-.101h.06l.07.002.045.003h.026l.07.005a4.041 4.041 0 0 1 1.635.49 3.94 3.94 0 0 1 1.602 1.662 3.77 3.77 0 0 1 .397 1.414l.005.076.003.075c.002.026.002.05.002.075 0 .024.003.052 0 .07v.065l-.002.073-.008.174a6.195 6.195 0 0 1-.08.639 5.1 5.1 0 0 1-.267.927 5.31 5.31 0 0 1-.624 1.13 5.052 5.052 0 0 1-3.237 2.014 4.82 4.82 0 0 1-.649.066l-.039.003h-.287a6.607 6.607 0 0 1-1.716-.265 6.776 6.776 0 0 1-3.4-2.274 6.75 6.75 0 0 1-.746-1.15 6.616 6.616 0 0 1-.714-2.596l-.005-.083-.002-.02v-.056l-.003-.073v-.096l-.003-.104v-.07l.003-.163c.008-.22.026-.45.054-.678a8.707 8.707 0 0 1 .28-1.355c.128-.444.286-.872.473-1.277a7.04 7.04 0 0 1 1.456-2.1 5.925 5.925 0 0 1 .953-.763c.169-.111.343-.213.524-.306.089-.05.182-.091.273-.135.047-.02.093-.042.138-.062a7.177 7.177 0 0 1 .714-.267l.145-.045c.049-.015.098-.026.148-.041.098-.029.197-.052.296-.076.049-.013.1-.02.15-.033l.15-.032.151-.028.076-.013.075-.01.153-.024c.057-.01.114-.013.171-.023l.169-.021c.036-.003.073-.008.106-.01l.073-.008.036-.003.042-.002c.057-.003.114-.008.171-.01l.086-.006h.023l.037-.003.145-.007a7.999 7.999 0 0 1 1.708.125 7.917 7.917 0 0 1 2.048.68 8.253 8.253 0 0 1 1.672 1.09l.09.077.089.078c.06.052.114.107.171.159.057.052.112.106.166.16.052.055.107.107.159.164a8.671 8.671 0 0 1 1.41 1.978c.012.026.028.052.04.078l.04.078.075.156c.023.051.05.1.07.153l.065.15a8.848 8.848 0 0 1 .45 1.34.19.19 0 0 0 .201.142.186.186 0 0 0 .172-.184c.01-.246.002-.532-.024-.856z"/>
  </svg>
);

GrafanaIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Mia-Platform brand mark — the official five-diamond icon, shared by every
// "always connected" platform service (Console, Catalog, Data Catalog)
const MiaPlatformIcon = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4.28887 2.05835L5.64944 2.84726V4.42508L4.28887 5.20186L2.92709 4.41538V2.84726L4.28887 2.05835ZM4.28887 1L2.01074 2.31445V4.94455L4.28887 6.26021L6.56579 4.94455V2.31445L4.28887 1Z" fill="#FF9933"/>
    <path d="M6.80914 6.42847L8.17335 7.21617V8.79399L6.80914 9.57076L5.44858 8.78549V7.21617L6.80914 6.4309V6.42847ZM6.80914 5.37254L4.53223 6.68456V9.31467L6.80914 10.6303L9.08727 9.31467V6.68456L6.80914 5.37012V5.37254Z" fill="#F03F44"/>
    <path d="M9.34063 10.7993L10.7012 11.5858V13.1636L9.34063 13.9489L7.97885 13.1563V11.5858L9.34063 10.8005V10.7993ZM9.34063 9.74096L7.0625 11.0554V13.6855L9.34063 15L11.6175 13.6855V11.0554L9.34063 9.73975V9.74096Z" fill="#1890FF"/>
    <path d="M4.27813 10.7993L5.6387 11.5858V13.1636L4.27813 13.9489L2.91756 13.1563V11.5858L4.27813 10.8005V10.7993ZM4.27813 9.74096L2 11.0554V13.6855L4.27813 15L6.55626 13.6855V11.0554L4.27813 9.73975V9.74096Z" fill="#26A570"/>
    <path d="M11.8484 6.54175L13.2102 7.32945V8.90727L11.8484 9.68404L10.4879 8.89877V7.32945L11.8484 6.54418V6.54175ZM11.8484 5.48583L9.57031 6.79784V9.42795L11.8484 10.7436L14.1266 9.42795V6.79784L11.8484 5.4834V5.48583Z" fill="#5F36DD"/>
  </svg>
);

MiaPlatformIcon.propTypes = {
  size: PropTypes.number.isRequired,
};

// Icon name to component mapping
const iconMap = {
  // Structure
  organization: OrganizationIcon,
  folder: FolderIcon,
  file: FileIcon,
  'file-plus': FilePlusIcon,
  'folder-plus': FolderPlusIcon,
  'code-file': CodeFileIcon,
  package: PackageIcon,
  
  // Actions
  refresh: RefreshIcon,
  eye: EyeIcon,
  warning: WarningIcon,
  trash: TrashIcon,
  analyze: AnalyzeIcon,
  design: DesignIcon,
  plan: PlanIcon,
  troubleshoot: TroubleshootIcon,
  link: LinkIcon,
  settings: SettingsIcon,
  tools: ToolsIcon,
  'mcp-server': McpServerIcon,
  code: CodeIcon,
  close: CloseIcon,
  fullscreen: FullscreenIcon,
  'exit-fullscreen': ExitFullscreenIcon,
  spinner: SpinnerIcon,
  'edit': EditIcon,
  
  // Media
  image: ImageIcon,
  video: VideoIcon,
  computer: ComputerIcon,
  document: DocumentIcon,
  camera: CameraIcon,
  
  // Languages/Tech
  react: ReactIcon,
  typescript: TypescriptIcon,
  javascript: JavascriptIcon,
  json: JsonIcon,
  html: HtmlIcon,
  css: CssIcon,
  python: PythonIcon,
  database: DatabaseIcon,
  docker: DockerIcon,
  git: GitIcon,
  github: GitHubIcon,
  gitlab: GitLabIcon,
  'azure-devops': AzureDevOpsIcon,
  bitbucket: BitbucketIcon,
  google: GoogleIcon,
  atlassian: AtlassianIcon,
  grafana: GrafanaIcon,
  'data-catalog': DataCatalogIcon,
  'mia-platform': MiaPlatformIcon,
  p4samd: P4SamdIcon,
  terminal: TerminalIcon,
  
  // New Home icons
  sparkles: SparklesIcon,
  paperclip: PaperclipIcon,
  microphone: MicrophoneIcon,
  'external-link': ExternalLinkIcon,
  'arrow-up': ArrowUpIcon,
  'arrow-right': ArrowRightIcon,
  'arrow-left': ArrowLeftIcon,
  template: TemplateIcon,
  'git-branch': GitBranchIcon,
  wand: WandIcon,
  search: SearchIcon,
  star: StarIcon,
  circle: CircleIcon,
  'check-circle': CheckCircleIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  rocket: RocketIcon,
  
  // Home Omnibar Mode icons
  'my-tasks': MyTasksIcon,
  brainstorming: BrainstormingIcon,
  'full-stack': FullStackIcon,
  'import-existing': ImportExistingIcon,
  modernize: ModernizeIcon,
  
  // Navigation icons
  grid: GridIcon,
  users: UsersIcon,
  compass: CompassIcon,
  book: BookIcon,
  gift: GiftIcon,
  zap: ZapIcon,
  bell: BellIcon,
  plus: PlusIcon,
  message: MessageIcon,
  'chevron-down': ChevronDownIcon,
  'chevron-up': ChevronUpIcon,
  'chevron-right': ChevronRightIcon,
  home: HomeIcon,
  menu: MenuIcon,
  'panel-left': PanelLeftIcon,
  'panel-right': PanelRightIcon,
  split: SplitIcon,
  layout: SplitIcon, // Alias for split
  robot: RobotIcon,
  loader: LoaderIcon,
  clock: ClockIcon,
  calendar: CalendarIcon,
  play: PlayIcon,
  pause: PauseIcon,
  list: ListIcon,
  x: XIcon,
  check: CheckIcon,
  info: InfoIcon,
  dollar: DollarIcon,
  'bar-chart': BarChartIcon,
  activity: ActivityIcon,
  sequence: SequenceIcon,
};

export default Icon;
